/**
 * Smart Cart Recommendations - Web Component
 * Custom element for managing the smart recommendation carousel
 *
 * Recommendations are rendered server-side via Liquid using product metafields.
 * This JS handles:
 * - Refreshing recommendations via AJAX section fetch
 * - Initializing product card functionality (quick-add, variants, etc.)
 * - Carousel navigation
 * - Hiding products when added from carousel
 */
class WilcoSmartCartRecs extends HTMLElement {
  constructor() {
    super();
    this.loaded = false;
    this.refreshing = false;
    // Cache DOM elements for performance
    this.elements = {};
  }

  connectedCallback() {
    // Cache frequently accessed DOM elements
    this.elements = {
      smartWrapper: this.querySelector('[data-smart-wrapper]'),
      fallbackWrapper: this.querySelector('[data-fallback-wrapper]'),
      contentState: this.querySelector('[data-smart-content]'),
      container: this.querySelector('[data-smart-content] .scroll-container')
    };

    // In the cart drawer the cards arrive in a <template> (not built at page load) and are
    // built when the drawer opens. Everywhere else they are set up right away, as before.
    if (this.querySelector('template[data-kott-deferred]')) {
      this._onDrawerOpen = () => this.buildDeferredCards(true);
      document.addEventListener('CartDrawer:open', this._onDrawerOpen);
    } else {
      this.initializeProductCards();
    }
    this.initNavigationButtons();
    this.loaded = true;
  }

  /**
   * Build the cards kept in <template data-kott-deferred> (assets/kott-deferred.js).
   * fromPageLoad: the cards came with the page, so they also get the setup page-load cards get
   * (incl. the 140px sizing theme.WilcoCartDrawerCarousel gives drawer cards at page load).
   */
  buildDeferredCards(fromPageLoad) {
    if (this._onDrawerOpen) {
      document.removeEventListener('CartDrawer:open', this._onDrawerOpen);
      this._onDrawerOpen = null;
    }
    if (!this.querySelector('template[data-kott-deferred]')) return;

    if (window.kottDeferred) {
      window.kottDeferred.buildIn(this);
    } else {
      this.querySelectorAll('template[data-kott-deferred]').forEach(tpl => tpl.replaceWith(tpl.content));
    }

    // After a refresh(), refresh() itself sets the new cards up, as before.
    if (!fromPageLoad) return;

    if (window.theme && theme.WilcoCartDrawerCarousel) {
      new theme.WilcoCartDrawerCarousel(this.closest('#HeaderCart') || this.parentElement);
    }
    this.initializeProductCards();
    this.updateSmartCartButtons();
  }

  disconnectedCallback() {
    // Clean up if needed
  }

  /**
   * Refresh recommendations by fetching updated section HTML from server
   * Called by HeaderCart when cart updates
   */
  async refresh(evt) {
    // If add was from carousel, item is already hidden - don't refresh
    if (evt?._fromSmartCarousel) {
      return;
    }

    // Prevent concurrent refreshes
    if (this.refreshing) {
      return;
    }
    this.refreshing = true;

    try {
      // Fetch updated section HTML from server
      const sectionUrl = this.dataset.sectionUrl || '/cart?sections=smart-cart-recs';
      const response = await fetch(sectionUrl);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // The response is a JSON object with section HTML
      const sectionHtml = data['smart-cart-recs'];

      if (sectionHtml) {
        // Parse and replace content
        const parser = new DOMParser();
        const doc = parser.parseFromString(sectionHtml, 'text/html');
        const newContent = doc.querySelector('wilco-smart-cart-recs');

        if (newContent) {
          // Replace inner content, preserving the element
          this.innerHTML = newContent.innerHTML;

          // Drawer markup keeps its cards in a <template>; the drawer is open or opening, build them now.
          this.buildDeferredCards(false);

          // Re-cache DOM elements after content replacement
          this.elements = {
            smartWrapper: this.querySelector('[data-smart-wrapper]'),
            fallbackWrapper: this.querySelector('[data-fallback-wrapper]'),
            contentState: this.querySelector('[data-smart-content]'),
            container: this.querySelector('[data-smart-content] .scroll-container')
          };

          // Re-initialize product card functionality
          this.initializeProductCards();
          this.initNavigationButtons();
        }
      }
    } catch (error) {
      console.error('[Smart Cart Recs] Refresh failed:', error);
    } finally {
      this.refreshing = false;
    }
  }

  /**
   * Hide specific product card when added from carousel
   * Provides smooth fade-out animation
   */
  hideProductInCarousel(variantId) {
    const container = this.elements.container || this.querySelector('[data-smart-content] .scroll-container');
    if (!container) return;

    const productCards = container.querySelectorAll('.product-item');
    if (!productCards) return;

    productCards.forEach(card => {
      const btn = card.querySelector('.quick-add-btn');
      const btnVariantId = btn?.dataset.currentVariant || btn?.dataset.variantId;

      if (btnVariantId === variantId.toString()) {
        // Fade out animation
        card.style.transition = 'opacity 0.3s ease-out';
        card.style.opacity = '0';

        // Remove from display after animation
        setTimeout(() => {
          card.style.display = 'none';
        }, 300);
      }
    });
  }

  /**
   * Initialize product card functionality
   * Sets up quick-add buttons, variant dropdowns, lazy loading, etc.
   */
  initializeProductCards() {
    const container = this.querySelector('[data-smart-content]') || this.querySelector('[data-fallback-wrapper]');
    if (!container) return;

    try {
      // Initialize lazyload for images (fix opacity 0 issue)
      const images = container.querySelectorAll('img[loading="lazy"], img.lazyload');
      images.forEach(img => {
        // Force load images by removing lazyload classes and setting opacity
        img.classList.remove('lazyload', 'lazyloading');
        img.classList.add('lazyloaded');
        img.style.opacity = '1';

        // If image has data-src, move it to src
        if (img.dataset.src && !img.src) {
          img.src = img.dataset.src;
        }
        if (img.dataset.srcset && !img.srcset) {
          img.srcset = img.dataset.srcset;
        }
      });

      // Initialize variant dropdowns if theme has this functionality
      if (window.theme && theme.ProductVariantDropdown) {
        const dropdowns = container.querySelectorAll('.product-variant-dropdown:not([data-initialized])');
        dropdowns.forEach(dropdown => {
          dropdown.setAttribute('data-initialized', 'true');
          new theme.ProductVariantDropdown(dropdown);
        });
      }

      // Initialize quick add to cart buttons
      if (window.theme && theme.QuickAddToCart) {
        const quickAddBtns = container.querySelectorAll('.quick-add-btn:not([data-initialized])');
        quickAddBtns.forEach(btn => {
          btn.setAttribute('data-initialized', 'true');

          // Remove any existing handler first to prevent duplicates
          if (btn._quickAddHandler) {
            btn.removeEventListener('click', btn._quickAddHandler);
          }

          // Create a new handler and store reference on the button
          btn._quickAddHandler = (evt) => {
            const variantId = btn.dataset.currentVariant || btn.dataset.variantId;

            // Mark event as from carousel so refresh() knows to skip
            evt._fromSmartCarousel = true;

            // Add to cart
            theme.QuickAddToCart.handleClick(evt);

            // Hide this card immediately (no refresh needed)
            if (variantId) {
              this.hideProductInCarousel(variantId);
            }
          };

          btn.addEventListener('click', btn._quickAddHandler);
        });
      }

      // Initialize any quick shop modals
      if (window.theme && theme.QuickShop) {
        new theme.QuickShop(container);
      }

      // Initialize QuickAdd if available
      if (window.theme && theme.QuickAdd) {
        new theme.QuickAdd(container);
      }

    } catch (error) {
      console.warn('[Smart Cart Recs] Failed to initialize product card functionality:', error);
    }
  }

  /**
   * Navigation button handlers for carousel scrolling
   */
  scrollSmartCartCarousel(direction) {
    const container = this.elements.container || this.querySelector('[data-smart-content] .scroll-container');
    if (!container) return;

    // Calculate scroll amount - 2 product widths
    const productWidth = 225 + 12; // product width + gap
    const scrollAmount = productWidth * 2;

    // Direction: 'left' or 'right'
    if (direction === 'left') {
      container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    } else {
      container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  }

  /**
   * Update navigation button visibility based on scroll position
   */
  updateSmartCartButtons() {
    const container = this.elements.container || this.querySelector('[data-smart-content] .scroll-container');
    const leftBtn = this.querySelector('[data-prev-button]');
    const rightBtn = this.querySelector('[data-next-button]');

    if (!container || !leftBtn || !rightBtn) return;

    const scrollWidth = container.scrollWidth;
    const clientWidth = container.clientWidth;
    const maxScroll = scrollWidth - clientWidth;

    if (maxScroll <= 0) {
      leftBtn.style.display = 'none';
      rightBtn.style.display = 'none';
    } else {
      leftBtn.style.display = 'flex';
      rightBtn.style.display = 'flex';
      // RTL: scrollLeft is negative. At start (rightmost) scrollLeft is 0, at end (leftmost) it's -maxScroll
      const scrollLeft = Math.abs(container.scrollLeft);
      const threshold = 5;
      // Disable "prev" (right arrow in RTL) when at rightmost position
      leftBtn.disabled = scrollLeft <= threshold;
      // Disable "next" (left arrow in RTL) when at leftmost position
      rightBtn.disabled = scrollLeft >= maxScroll - threshold;
    }
  }

  /**
   * Initialize carousel navigation buttons
   */
  initNavigationButtons() {
    const leftBtn = this.querySelector('[data-prev-button]');
    const rightBtn = this.querySelector('[data-next-button]');
    const container = this.elements.container || this.querySelector('[data-smart-content] .scroll-container');

    if (!leftBtn || !rightBtn || !container) return;

    // Remove existing listeners if any
    if (this._navButtonsInitialized) return;
    this._navButtonsInitialized = true;

    // Prev arrow (RTL: scrolls right to go back)
    leftBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      this.scrollSmartCartCarousel('right');
    }, true);

    // Next arrow (RTL: scrolls left to go forward)
    rightBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      this.scrollSmartCartCarousel('left');
    }, true);

    // Update button states on scroll
    container.addEventListener('scroll', () => {
      this.updateSmartCartButtons();
    });

    // Update button states on resize
    if (window.ResizeObserver) {
      const resizeObserver = new ResizeObserver(() => {
        this.updateSmartCartButtons();
      });
      resizeObserver.observe(container);
    }

    // Initial button state with multiple delays to ensure content is loaded
    setTimeout(() => this.updateSmartCartButtons(), 100);
    setTimeout(() => this.updateSmartCartButtons(), 300);
    setTimeout(() => this.updateSmartCartButtons(), 500);
  }
}

// Register the custom element
if (!customElements.get('wilco-smart-cart-recs')) {
  customElements.define('wilco-smart-cart-recs', WilcoSmartCartRecs);
}

// Log initialization
console.log('[Smart Cart Recs] Module loaded - using metafield-based recommendations');
